import { useState, useMemo } from "react";
import { ArrowLeft, Package, DollarSign, Info, CheckCircle2, AlertCircle, Check } from "lucide-react";
import { BottomNav } from "./bottom-nav";
import { CashAmountInput } from "./cash-amount-input";
import { AdImage } from "@/app/components/common/ad-image";
import { getAdImage } from "@/app/utils/ad-utils";
import { toast } from "sonner";
import type { Ad } from "./ad-card";

interface MakeOfferPageProps {
  targetAd: Ad;
  userAds: Ad[];
  /** True while user's ads are loading (avoids flashing "precisa ter anúncio ativo"). */
  userAdsLoading?: boolean;
  onBack: () => void;
  onSubmitOffer: (offer: OfferData) => void;
  /** True while the offer is being submitted (keeps user on page, shows alert before any redirect). */
  isSubmittingOffer?: boolean;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export interface OfferData {
  targetAdId: string;
  /** Snapshot when ad is not in home feed (e.g. opened from ad detail). */
  targetAdTitle?: string;
  targetAdSeller?: string;
  targetAdPrice?: number;
  targetAdImage?: string;
  offerType: "cash-only" | "product-plus-cash";
  cashAmount: number;
  tradeInAds?: Array<{ id: string; value: number; title: string }>;
  message?: string;
  totalOfferValue: number;
}

type OfferStep = "select-type" | "select-product" | "enter-amount" | "review";

// Constants
const MESSAGE_MAX_LENGTH = 500;
const STEP_LABELS = ["Tipo", "Detalhes", "Revisão"];

// Helper Functions
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

// Reusable Components
interface InfoBoxProps {
  title: string;
  description: string | React.ReactNode;
  icon?: React.ReactNode;
}

function InfoBox({ title, description, icon = <Info className="w-5 h-5 text-primary" /> }: InfoBoxProps) {
  return (
    <div className="bg-secondary/30 border border-primary/20 rounded-xl p-4">
      <div className="flex gap-3">
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div>
          <h3 className="font-medium text-foreground mb-1">{title}</h3>
          {typeof description === "string" ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : (
            <div className="text-sm text-muted-foreground">{description}</div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ProductCardProps {
  ad: Ad;
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  compact?: boolean;
}

function ProductCard({ ad, isSelectable = false, isSelected = false, onSelect, compact = false }: ProductCardProps) {
  const Component = isSelectable ? "button" : "div";
  const imageSize = compact ? "w-16 h-16" : "w-20 h-20";

  return (
    <Component
      onClick={onSelect}
      className={`${isSelectable ? "w-full" : ""} bg-card border-2 rounded-xl p-${compact ? "3" : "4"} transition-all text-left ${
        isSelectable ? (isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50") : "border-border"
      }`}
    >
      <div className="flex gap-3">
        {isSelectable && (
          <div className="flex-shrink-0 pt-1">
            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
              isSelected ? "bg-primary border-primary" : "border-border bg-background"
            }`}>
              {isSelected && <Check className="w-4 h-4 text-white" />}
            </div>
          </div>
        )}
        <AdImage src={getAdImage(ad)} alt={ad.title} className={`${imageSize} rounded-lg object-cover`} />
        <div className="flex-1">
          <h3 className={`font-medium text-foreground ${compact ? "text-sm" : ""} mb-1`}>{ad.title}</h3>
          {!compact && <p className="text-sm text-muted-foreground mb-2">{ad.category || ad.location}</p>}
          <p className={`${compact ? "text-lg" : "text-lg"} font-semibold text-primary`}>{formatCurrency(ad.price)}</p>
        </div>
      </div>
    </Component>
  );
}

interface StepIndicatorProps {
  currentStep: number;
}

function StepIndicator({ currentStep }: StepIndicatorProps) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {STEP_LABELS.map((step, index) => (
        <div key={step} className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
            index <= currentStep ? "bg-primary text-white" : "bg-muted text-muted-foreground"
          }`}>
            {index + 1}
          </div>
          {index < STEP_LABELS.length - 1 && (
            <div className={`w-12 h-0.5 ${index < currentStep ? "bg-primary" : "bg-muted"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export function MakeOfferPage({
  targetAd,
  userAds,
  userAdsLoading = false,
  onBack,
  onSubmitOffer,
  isSubmittingOffer = false,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: MakeOfferPageProps) {
  const [currentStep, setCurrentStep] = useState<OfferStep>("select-type");
  const [offerType, setOfferType] = useState<"cash-only" | "product-plus-cash">("cash-only");
  const [selectedTradeInAds, setSelectedTradeInAds] = useState<Array<{ id: string; value: number; title: string }>>([]);
  const [cashAmount, setCashAmount] = useState("");
  const [displayAmount, setDisplayAmount] = useState("");
  const [message, setMessage] = useState("");

  // Filter out inactive ads and ads without prices
  const activeUserAds = userAds.filter(ad => ad.price && ad.price > 0);

  // Memoized calculations
  const totalTradeInValue = useMemo(() => {
    return selectedTradeInAds.reduce((sum, ad) => sum + ad.value, 0);
  }, [selectedTradeInAds]);

  const cashValue = useMemo(() => parseFloat(cashAmount) || 0, [cashAmount]);

  const totalOfferValue = useMemo(() => {
    return totalTradeInValue + cashValue;
  }, [totalTradeInValue, cashValue]);

  const difference = useMemo(() => {
    return targetAd.price - totalOfferValue;
  }, [targetAd.price, totalOfferValue]);

  const stepIndex = useMemo(() => {
    switch (currentStep) {
      case "select-type":
        return 0;
      case "select-product":
      case "enter-amount":
        return 1;
      case "review":
        return 2;
      default:
        return 0;
    }
  }, [currentStep]);

  const handleCashAmountChange = (numericValue: string, formattedValue: string) => {
    setCashAmount(numericValue);
    setDisplayAmount(formattedValue);
  };

  const handleSelectOfferType = (type: "cash-only" | "product-plus-cash") => {
    setOfferType(type);
    if (type === "cash-only") {
      setSelectedTradeInAds([]);
      setCurrentStep("enter-amount");
    } else {
      setCurrentStep("select-product");
    }
  };

  const handleSelectTradeInAd = (ad: Ad) => {
    const existingAd = selectedTradeInAds.find(a => a.id === ad.id);
    if (existingAd) {
      setSelectedTradeInAds(selectedTradeInAds.filter(a => a.id !== ad.id));
    } else {
      setSelectedTradeInAds([...selectedTradeInAds, { id: ad.id, value: ad.price, title: ad.title }]);
    }
  };

  const handleContinueToReview = () => {
    if (!cashAmount || parseFloat(cashAmount) < 0) {
      toast.error("Por favor, insira um valor válido");
      return;
    }
    setCurrentStep("review");
  };

  const handleSubmitOffer = () => {
    const offer: OfferData = {
      targetAdId: targetAd.id,
      targetAdTitle: targetAd.title,
      targetAdSeller: targetAd.seller,
      targetAdPrice: targetAd.price,
      targetAdImage: getAdImage(targetAd) || undefined,
      offerType,
      cashAmount: cashValue,
      tradeInAds: selectedTradeInAds.map(ad => ({ id: ad.id, value: ad.value, title: ad.title })),
      message: message.trim(),
      totalOfferValue: totalOfferValue,
    };

    onSubmitOffer(offer);
  };

  const getAdById = (id: string) => {
    return userAds.find(ad => ad.id === id);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Fazer Oferta</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <StepIndicator currentStep={stepIndex} />

        {/* Target Ad Info */}
        <div className="mb-6 bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground mb-2">Você está fazendo uma oferta para:</p>
          <div className="flex gap-3">
            <AdImage
              src={getAdImage(targetAd)}
              alt={targetAd.title}
              className="w-20 h-20 rounded-lg object-cover"
            />
            <div className="flex-1">
              <h3 className="font-medium text-foreground">{targetAd.title}</h3>
              <p className="text-sm text-muted-foreground mb-1">{targetAd.location}</p>
              <p className="text-lg font-semibold text-primary">{formatCurrency(targetAd.price)}</p>
            </div>
          </div>
        </div>

        {/* Step 1: Select Offer Type */}
        {currentStep === "select-type" && (
          <div className="space-y-4">
            <InfoBox
              title="Como funciona?"
              description="Você pode fazer uma oferta em dinheiro ou oferecer um de seus produtos como parte do pagamento."
            />

            <button
              onClick={() => handleSelectOfferType("cash-only")}
              className="w-full bg-card border-2 border-border hover:border-primary rounded-xl p-4 transition-all text-left"
            >
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground mb-1">Oferta em Dinheiro</h3>
                  <p className="text-sm text-muted-foreground">
                    Faça uma oferta somente com pagamento em dinheiro via plataforma
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleSelectOfferType("product-plus-cash")}
              className="w-full bg-card border-2 border-border hover:border-primary rounded-xl p-4 transition-all text-left"
              disabled={userAdsLoading || activeUserAds.length === 0}
            >
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground mb-1">Produto + Dinheiro</h3>
                  <p className="text-sm text-muted-foreground">
                    Ofereça um dos seus produtos como parte do pagamento e pague a diferença
                  </p>
                  {userAdsLoading && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Carregando seus anúncios…
                    </p>
                  )}
                  {!userAdsLoading && activeUserAds.length === 0 && (
                    <p className="text-xs text-destructive mt-2">
                      Você precisa ter pelo menos um anúncio ativo para usar esta opção
                    </p>
                  )}
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Step 2: Select Product (for product-plus-cash) */}
        {currentStep === "select-product" && (
          <div className="space-y-4 pb-64">
            <InfoBox
              title="Escolha seus produtos"
              description="Selecione um ou mais produtos que você deseja oferecer como parte do pagamento"
            />
            <button
              onClick={() => setCurrentStep("select-type")}
              className="text-sm text-primary hover:underline mb-4"
            >
              ← Voltar para tipo de oferta
            </button>

            {activeUserAds.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-8 text-center">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Você não tem anúncios ativos</p>
              </div>
            ) : (
              <>
                {selectedTradeInAds.length > 0 && (
                  <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {selectedTradeInAds.length} {selectedTradeInAds.length === 1 ? "produto selecionado" : "produtos selecionados"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Valor total: {formatCurrency(totalTradeInValue)}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedTradeInAds([])}
                        className="text-sm text-primary hover:underline"
                      >
                        Limpar seleção
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {activeUserAds.map((ad) => {
                    const isSelected = selectedTradeInAds.some(a => a.id === ad.id);
                    return (
                      <ProductCard
                        key={ad.id}
                        ad={ad}
                        isSelectable={true}
                        isSelected={isSelected}
                        onSelect={() => handleSelectTradeInAd(ad)}
                      />
                    );
                  })}
                </div>

                {/* Spacer so last product can scroll fully above the fixed button bar (bar is bottom-20 + ~5rem height) */}
                <div className="h-32 shrink-0" aria-hidden />

                {/* Fixed above bottom nav so it stays visible when scrolling */}
                <div className="fixed left-0 right-0 z-50 p-4 pt-3 pb-4 bg-background border-t border-border shadow-[0_-4px_12px_rgba(0,0,0,0.08)] bottom-20"
                >
                  <div className="max-w-7xl mx-auto">
                    <button
                      onClick={() => setCurrentStep("enter-amount")}
                      disabled={selectedTradeInAds.length === 0}
                      className="w-full bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground text-white py-4 rounded-xl font-medium transition-colors shadow-lg"
                    >
                    {selectedTradeInAds.length === 0 
                      ? "Selecione pelo menos um produto" 
                      : `Continuar com ${selectedTradeInAds.length} ${selectedTradeInAds.length === 1 ? "produto" : "produtos"}`}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 3: Enter Cash Amount */}
        {currentStep === "enter-amount" && (
          <div className="space-y-4">
            <button
              onClick={() => setCurrentStep(offerType === "cash-only" ? "select-type" : "select-product")}
              className="text-sm text-primary hover:underline mb-4"
            >
              ← Voltar
            </button>

            {/* Sticky Summary Card - Always Visible */}
            <div className="sticky top-0 z-30 -mx-4 px-4 py-4 bg-background/95 backdrop-blur-sm border-b border-border mb-4">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 rounded-xl p-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Resumo da Oferta</h3>
                
                <div className="space-y-2">
                  {/* Product Price */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-foreground">Preço do anúncio:</span>
                    <span className="font-semibold text-foreground">{formatCurrency(targetAd.price)}</span>
                  </div>

                  {/* Trade-in Value */}
                  {selectedTradeInAds.length > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-foreground">
                        {selectedTradeInAds.length === 1 ? "Seu produto:" : "Seus produtos:"}
                      </span>
                      <span className="font-semibold text-primary">- {formatCurrency(totalTradeInValue)}</span>
                    </div>
                  )}

                  {/* Cash Amount */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-foreground">Pagamento em dinheiro:</span>
                    <span className="font-semibold text-primary">
                      + {formatCurrency(cashValue)}
                    </span>
                  </div>

                  {/* Divider */}
                  <div className="border-t-2 border-primary/20 my-2"></div>

                  {/* Total Offer */}
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-foreground">Total da Oferta:</span>
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(totalOfferValue)}
                    </span>
                  </div>

                  {/* Difference Indicator */}
                  {cashValue > 0 && (
                    <div className={`mt-3 px-3 py-2 rounded-lg text-center ${
                      difference > 0 
                        ? "bg-orange-100 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400" 
                        : difference < 0
                        ? "bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400"
                        : "bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400"
                    }`}>
                      <p className="text-xs font-medium">
                        {difference > 0 
                          ? `Faltam ${formatCurrency(Math.abs(difference))} para o preço total` 
                          : difference < 0
                          ? `Sua oferta está ${formatCurrency(Math.abs(difference))} acima do preço`
                          : "Sua oferta está igual ao preço solicitado"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {selectedTradeInAds.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-3">
                  {selectedTradeInAds.length === 1 ? "Produto selecionado:" : "Produtos selecionados:"}
                </p>
                <div className="space-y-3">
                  {selectedTradeInAds.map(selectedAd => {
                    const fullAd = getAdById(selectedAd.id);
                    return (
                      <div key={selectedAd.id} className="flex gap-3 bg-background rounded-lg p-3">
                        {fullAd && (
                          <AdImage
                            src={getAdImage(fullAd)}
                            alt={selectedAd.title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-medium text-foreground text-sm">{selectedAd.title}</h3>
                          <p className="text-lg font-semibold text-primary">{formatCurrency(selectedAd.value)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <InfoBox
              title="Valor em dinheiro"
              description={
                offerType === "cash-only" 
                  ? "Insira o valor que você deseja oferecer pelo produto"
                  : "Insira o valor em dinheiro que você pagará além do produto oferecido"
              }
            />

            {/* Difference to Pay - Prominent with Quick Fill Button */}
            {selectedTradeInAds.length > 0 && (
              <div className="bg-gradient-to-r from-primary/20 to-primary/10 border-2 border-primary rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Diferença a Pagar</p>
                    <p className="text-3xl font-bold text-primary">
                      {formatCurrency(Math.max(0, targetAd.price - totalTradeInValue))}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const diff = Math.max(0, targetAd.price - totalTradeInValue);
                      const formatted = new Intl.NumberFormat("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(diff);
                      handleCashAmountChange(diff.toString(), formatted);
                    }}
                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition-colors shadow-md"
                  >
                    Usar este valor
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Este é o valor restante para atingir o preço do anúncio
                </p>
              </div>
            )}

            {/* Cash Amount Input */}
            <CashAmountInput
              value={cashAmount}
              displayValue={displayAmount}
              onChange={handleCashAmountChange}
              label="Valor em Dinheiro"
              placeholder="0,00"
              locale="pt-BR"
              currency="BRL"
            />

            {/* Optional Message */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Mensagem (Opcional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Adicione uma mensagem ao vendedor..."
                rows={4}
                maxLength={MESSAGE_MAX_LENGTH}
                className="w-full px-4 py-3 bg-white dark:bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1 text-right">
                {message.length}/{MESSAGE_MAX_LENGTH} caracteres
              </p>
            </div>

            {/* Difference Alert */}
            {cashAmount && parseFloat(cashAmount) > 0 && (
              <div className={`rounded-xl p-4 ${
                difference > 0 
                  ? "bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900" 
                  : "bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900"
              }`}>
                <div className="flex gap-3">
                  {difference > 0 ? (
                    <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      {difference > 0 
                        ? `Sua oferta está ${formatCurrency(Math.abs(difference))} abaixo do preço` 
                        : difference < 0
                        ? `Sua oferta está ${formatCurrency(Math.abs(difference))} acima do preço`
                        : "Sua oferta está igual ao preço"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {difference > 0 
                        ? "O vendedor pode aceitar, recusar ou fazer uma contraproposta" 
                        : "Você está oferecendo o valor total ou mais"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleContinueToReview}
              disabled={!cashAmount || parseFloat(cashAmount) <= 0}
              className="w-full bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-lg shadow-lg"
            >
              {!cashAmount || parseFloat(cashAmount) <= 0 ? (
                "Digite um valor para continuar"
              ) : (
                <>
                  Revisar Oferta de {formatCurrency(totalOfferValue)}
                  <ArrowLeft className="w-5 h-5 rotate-180" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === "review" && (
          <div className="space-y-4">
            <button
              onClick={() => setCurrentStep("enter-amount")}
              className="text-sm text-primary hover:underline mb-4"
            >
              ← Editar oferta
            </button>

            <InfoBox
              title="Revise sua oferta"
              description="Confira todos os detalhes antes de enviar sua proposta ao vendedor"
              icon={<CheckCircle2 className="w-5 h-5 text-primary" />}
            />

            {/* Offer Summary */}
            <div className="bg-card border border-border rounded-xl p-4 space-y-4">
              <h3 className="font-medium text-foreground">Resumo da Oferta</h3>

              {selectedTradeInAds.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {selectedTradeInAds.length === 1 ? "Produto oferecido:" : "Produtos oferecidos:"}
                  </p>
                  <div className="space-y-2">
                    {selectedTradeInAds.map(selectedAd => {
                      const fullAd = getAdById(selectedAd.id);
                      return (
                        <div key={selectedAd.id} className="flex gap-3 bg-background rounded-lg p-3">
                          {fullAd && (
                            <AdImage
                              src={getAdImage(fullAd)}
                              alt={selectedAd.title}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-medium text-foreground text-sm">{selectedAd.title}</h4>
                            <p className="text-lg font-semibold text-primary">{formatCurrency(selectedAd.value)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {selectedTradeInAds.length > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Valor dos produtos:</span>
                    <span className="font-medium text-foreground">{formatCurrency(totalTradeInValue)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Pagamento em dinheiro:</span>
                  <span className="font-medium text-foreground">{formatCurrency(cashValue)}</span>
                </div>
                <div className="border-t border-border pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-foreground">Valor total da oferta:</span>
                    <span className="text-xl font-bold text-primary">
                      {formatCurrency(totalOfferValue)}
                    </span>
                  </div>
                </div>
              </div>

              {message && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Mensagem:</p>
                  <div className="bg-background rounded-lg p-3">
                    <p className="text-sm text-foreground">{message}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Target Ad Summary */}
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-sm text-muted-foreground mb-2">Produto desejado:</p>
              <div className="flex gap-3">
                <AdImage
                  src={getAdImage(targetAd)}
                  alt={targetAd.title}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">{targetAd.title}</h3>
                  <p className="text-sm text-muted-foreground mb-1">{targetAd.location}</p>
                  <p className="text-lg font-semibold text-foreground">{formatCurrency(targetAd.price)}</p>
                </div>
              </div>
            </div>

            {/* Important Info */}
            <InfoBox
              title="Informações Importantes"
              description={
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="text-primary">•</span>
                    <span>O vendedor tem 24h para responder sua oferta</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">•</span>
                    <span>O pagamento só será processado após aceitação</span>
                  </li>
                  {selectedTradeInAds.length > 0 && (
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>Seus produtos ficarão reservados até a resposta do vendedor</span>
                    </li>
                  )}
                  <li className="flex gap-2">
                    <span className="text-primary">•</span>
                    <span>Você será notificado sobre a resposta</span>
                  </li>
                </ul>
              }
            />

            <button
              onClick={handleSubmitOffer}
              disabled={isSubmittingOffer}
              className="w-full py-4 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:opacity-70 disabled:pointer-events-none"
              style={{
                backgroundColor: 'var(--orange)',
                color: 'var(--orange-foreground)',
              }}
              onMouseEnter={(e) => {
                if (!isSubmittingOffer) e.currentTarget.style.backgroundColor = 'var(--orange-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--orange)';
              }}
            >
              {isSubmittingOffer ? "Enviando…" : "Enviar Oferta"}
            </button>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}